import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Publicacion } from '../novedades/api';

/**
 * api.ts — el contrato de Comunidades. Módulo 9.
 *
 * ## Los quince endpoints son los mismos
 *
 * Ninguno cambia de firma ni de parámetros. Lo que sí cambió, y va escrito
 * aparte, son **cinco comparaciones de rol por su nombre** dentro de
 * `communities.py`: `user.role in ("coordinador", "admin")` deja fuera a los
 * roles personalizados marcados como gestión, que es justo lo que
 * `is_staff_user` existe para respetar. El propio archivo ya lo hacía bien en
 * `community_posts`, con un comentario explicándolo; las otras cinco se
 * quedaron atrás. Lo vigila `test_comunidades_pantalla.py`.
 *
 * ## Por qué las publicaciones viven aquí y no en Novedades
 *
 * El endpoint es de la comunidad —`/communities/{id}/posts`, no `/posts`— pero
 * lo que devuelve es exactamente lo que devuelve el muro: `_post_dict`, el mismo
 * ayudante del mismo router. Por eso el tipo se importa de Novedades en vez de
 * copiarse: si `_post_dict` gana un campo, lo gana una sola declaración.
 *
 * La tarjeta que las pinta también es la de Novedades. Hasta hoy eso obligaba a
 * una raíz React incrustada dentro de una pantalla vanilla
 * (`montarPublicaciones` en el puente); al migrar esta pantalla, esa puerta se
 * cierra y el componente se usa directamente.
 */

/* ── Tipos ─────────────────────────────────────────────────────────────── */

export type RolDeMiembro = 'member' | 'moderator';

/** Lo que devuelve `_community_dict` (`communities.py:101`). */
export interface Comunidad {
  id: number;
  name: string;
  description?: string | null;
  /** Normas de convivencia. Las escribe un moderador y salen con
   *  `white-space: pre-line` en la cabecera que ven todos los miembros. */
  rules?: string | null;
  area_id?: number | null;
  area_name?: string | null;
  is_private: boolean;
  /** Nombre de clase de icono. Desde el 2026-09-01 ya no es un emoji, pero las
   *  filas antiguas pueden seguir trayéndolo: `Utils.iconHtml` pinta las dos. */
  icon: string;
  /** Color de portada, elegido con un `<input type="color">` sin límites. El
   *  título va en blanco encima, así que un color claro lo deja ilegible: hoy
   *  las dos comunidades reales dan 5,87 y 14,26, pero nada lo impide. */
  color: string;
  member_count: number;
  post_count: number;
  is_member: boolean;
  my_role?: RolDeMiembro | null;
  can_moderate: boolean;
}

/** Lo que devuelve `list_members`. Trae `avatar_url` —presignada— y el vanilla
 *  no lo usaba: pintaba iniciales sobre la marca, que daban 2,52 de contraste. */
export interface Miembro {
  user_id: number;
  full_name: string;
  position?: string | null;
  role: RolDeMiembro;
  avatar_url?: string | null;
}

export interface AreaDelSelector {
  id: number;
  name: string;
}

/** Lo que se manda al crear o editar. Mismo cuerpo en `POST` y en `PUT`. */
export interface CuerpoDeComunidad {
  name: string;
  description: string | null;
  rules: string | null;
  area_id: number | null;
  is_private: boolean;
  icon: string;
  color: string;
}

export type { Publicacion };

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  todo: ['comunidades'] as const,
  lista: ['comunidades', 'lista'] as const,
  detalle: (id: number) => ['comunidades', 'detalle', id] as const,
  publicaciones: (id: number) => ['comunidades', 'publicaciones', id] as const,
  miembros: (id: number) => ['comunidades', 'miembros', id] as const,
  areas: ['comunidades', 'areas'] as const,
};

/* ── Lectura ───────────────────────────────────────────────────────────── */

export function useComunidades() {
  return useQuery({
    queryKey: claves.lista,
    queryFn: () => api.get<Comunidad[]>('/communities/'),
  });
}

export function useComunidad(id: number | null) {
  return useQuery({
    queryKey: claves.detalle(id ?? 0),
    queryFn: () => api.get<Comunidad>(`/communities/${id}`),
    enabled: id != null,
  });
}

/**
 * El muro de una comunidad.
 *
 * `habilitado` es lo que decide si se pide: en una comunidad privada de la que
 * no se es miembro el servidor responde 403, y pedirlo para enseñar el error
 * sería pedirle al servidor que repita lo que la propia respuesta anterior ya
 * dijo. La pantalla enseña el aviso de «únete para ver» sin gastar la petición.
 */
export function usePublicacionesDeComunidad(id: number | null, habilitado: boolean) {
  return useQuery({
    queryKey: claves.publicaciones(id ?? 0),
    queryFn: () => api.get<Publicacion[]>(`/communities/${id}/posts`, { limit: 30 }),
    enabled: id != null && habilitado,
  });
}

export function useMiembros(id: number | null) {
  return useQuery({
    queryKey: claves.miembros(id ?? 0),
    queryFn: () => api.get<Miembro[]>(`/communities/${id}/members`),
    enabled: id != null,
  });
}

/** Las áreas del selector del formulario. Se piden una sola vez por sesión de
 *  pantalla: no cambian mientras alguien crea una comunidad. */
export function useAreas(habilitado: boolean) {
  return useQuery({
    queryKey: claves.areas,
    queryFn: () => api.get<AreaDelSelector[]>('/users/areas'),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
}

/* ── Escritura ─────────────────────────────────────────────────────────── */

interface RespuestaDePublicar {
  id: number;
  requires_ack: boolean;
  avisados: number;
}

/**
 * Publicar en el muro de una comunidad.
 *
 * Los adjuntos van **después**, al endpoint genérico `/posts/{id}/attachments`,
 * porque hasta que la publicación no existe no hay a qué adjuntarlos. Los que
 * fallen se devuelven en `fallidos` en vez de tumbar la publicación: el texto ya
 * está publicado y decir «no se pudo publicar» sería mentir.
 */
export function usePublicarEnComunidad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      comunidadId: number;
      contenido: string;
      exigirLectura: boolean;
      archivos: File[];
    }) => {
      const res = await api.post<RespuestaDePublicar>(`/communities/${v.comunidadId}/posts`, {
        content: v.contenido,
        ...(v.exigirLectura ? { requires_ack: true } : {}),
      });
      const fallidos: File[] = [];
      for (const f of v.archivos) {
        const fd = new FormData();
        fd.append('file', f);
        try {
          await api.upload(`/posts/${res.id}/attachments`, fd);
        } catch {
          fallidos.push(f);
        }
      }
      return { ...res, fallidos };
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: claves.publicaciones(v.comunidadId) });
      void qc.invalidateQueries({ queryKey: claves.detalle(v.comunidadId) });
      void qc.invalidateQueries({ queryKey: claves.lista });
      // El muro global NO se toca: una publicación de comunidad no sale en él
      // —`content.py` filtra `community_id IS NULL`— ni en la búsqueda global.
    },
  });
}

/** Unirse. Refresca el detalle, los miembros y la lista: cambian `is_member` y
 *  `member_count`. */
export function useUnirse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<{ message?: string }>(`/communities/${id}/join`, null),
    onSuccess: (_d, id) => {
      void qc.invalidateQueries({ queryKey: claves.detalle(id) });
      void qc.invalidateQueries({ queryKey: claves.miembros(id) });
      void qc.invalidateQueries({ queryKey: claves.lista });
    },
  });
}

/**
 * Salir.
 *
 * `seVa`: quien sale de una PRIVADA sin ser de gestión deja de poder verla. Ahí
 * no se vuelve a pedir el detalle —el servidor respondería 403 y la pantalla
 * pintaría un error justo después del «Saliste de la comunidad»— y la pantalla
 * se va a la lista.
 */
export function useSalir() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; seVa: boolean }) =>
      api.post<{ message?: string }>(`/communities/${v.id}/leave`, null),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: claves.lista });
      if (v.seVa) return;
      void qc.invalidateQueries({ queryKey: claves.detalle(v.id) });
      void qc.invalidateQueries({ queryKey: claves.miembros(v.id) });
    },
  });
}

export function useGuardarComunidad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number | null; cuerpo: CuerpoDeComunidad }) =>
      v.id
        ? api.put<Comunidad>(`/communities/${v.id}`, v.cuerpo)
        : api.post<Comunidad>('/communities/', v.cuerpo),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: claves.lista });
      if (v.id) void qc.invalidateQueries({ queryKey: claves.detalle(v.id) });
    },
  });
}

export function useAgregarMiembro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { comunidadId: number; userId: number }) =>
      api.post<{ message: string }>(`/communities/${v.comunidadId}/members`, {
        user_id: v.userId,
        role: 'member',
      }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: claves.miembros(v.comunidadId) });
      void qc.invalidateQueries({ queryKey: claves.detalle(v.comunidadId) });
      void qc.invalidateQueries({ queryKey: claves.lista });
    },
  });
}

export function useCambiarRol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { comunidadId: number; userId: number; rol: RolDeMiembro }) =>
      api.put<{ message: string }>(`/communities/${v.comunidadId}/members/${v.userId}`, {
        user_id: v.userId,
        role: v.rol,
      }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: claves.miembros(v.comunidadId) });
      void qc.invalidateQueries({ queryKey: claves.detalle(v.comunidadId) });
    },
  });
}

export function useQuitarMiembro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { comunidadId: number; userId: number }) =>
      api.del<{ message: string }>(`/communities/${v.comunidadId}/members/${v.userId}`),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: claves.miembros(v.comunidadId) });
      void qc.invalidateQueries({ queryKey: claves.detalle(v.comunidadId) });
      void qc.invalidateQueries({ queryKey: claves.lista });
    },
  });
}
